import React, { useEffect, useState } from 'react';
import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { useInvitesCRUDActions } from '@mm/state/actions/InvitesCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import { selectCompanyUsers } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { CrmIntegrationsTypes } from '../../Integrations/BaseIntegration/BaseIntegration';
import { IntegrationLinkType } from '../../Integrations/IntegrationsPanel/IntegrationsPanel';
import { useAdAccount } from '../../Integrations/NativeIntegrations/components/FacebookAdsIntegrations/use-ad-account';
import { ActionProps } from './ActivationBlock';
import {
  CrownOutlined,
  TeamOutlined,
  NotificationOutlined,
  ApiOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { ReactComponent as FacebookIcon } from 'assets/facebook-circle.svg';
import { XRayIconSquare as XRayIcon } from '@mm/icons';
import { ReactComponent as UserFilled } from 'assets/user-filled.svg';
import { ReactComponent as AdWordIcon } from 'assets/adword.svg';
import css from './ActivationBlock.module.css';
import { IntegrationIcon } from '../../Integrations/IntegrationIcon/IntegrationIcon';
import { uniqueId } from 'lodash';
import { Tooltip } from '@mm/components';
import classNames from 'classnames';
import { selectGoogleClientCredentials } from '@mm/state/selectors/googleAdsSelectors';
import { useGoogleAdsCRUDActions } from '@mm/state/actions/GoogleAdsCRUDActions';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

type Crm = {
  integrationType?: string;
  active?: boolean;
};

export const humanizedIntegrationNames = {
  json_webhook: 'Webhook',
  default: 'Webhook',
  mailchimp: 'Mailchimp',
  zapier: 'Zapier',
  google_spreadsheet: 'Google Sheets',
  klaviyo: 'Klaviyo',
  salesforce: 'SalesForce',
  sendlane: 'Sendlane',
  shopify: 'Shopify',
  slack: 'Slack',
  gohighlevel: 'HighLevel',
  hubspot: 'HubSpot',
  omnisend: 'Omnisend',
};

export const useActivationSteps = (crmsList?: string[]) => {
  const plans = useSelector(enhancedPlansSelector);
  const company = useSelector(activeCompanySelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const googleClientCredentials = useSelector(selectGoogleClientCredentials);
  const invites = useSelector((state: StoreState) => state.invite.entities);
  const users = useSelector(selectCompanyUsers);
  const isShopify = useSelector(selectIsShopifyContext);
  const pagesWithAvailableSeats =
    useSelector((state: StoreState) => state.invite.pagesWithAvailableSeats) ||
    [];

  const activeAndPendingUsers = [
    ...invites.map(invite => ({ email: invite.email, pending: true })),
    ...users.map(user => ({ email: user.email, pending: false })),
  ];
  const [crms, setCrms] = useState<Crm[] | []>([]);

  const { retriveAddedCrms } = useConnectionCRUDActions();
  const { retrieveAll } = useInvitesCRUDActions();
  const { retrieveGoogleClientCredentials } = useGoogleAdsCRUDActions();
  const { openNewBillingModal2023, openInviteUserModal } = useModalActions();
  const { getAccounts } = useEmailAccountCRUDActions();
  const adAccount = useAdAccount();
  const history = useHistory();

  const hasFacebookAdAccounts = !!adAccount?.facebookPageAdAccounts?.length;
  const hasGoogleAdsAccount = !!googleClientCredentials.length;
  const hasAdAccounts = hasFacebookAdAccounts || hasGoogleAdsAccount;

  const currentPlanName = plans.find(
    p => p.planCode === facebookPage?.subscription?.planCode,
  )?.shortName;

  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

  const choosePlan = () => {
    openNewBillingModal2023({
      landmineName: landmineNames.DASHBOARD_CHOOSE_PLAN_ACTION,
    });
  };

  const navigateTo = (
    kind: IntegrationLinkType,
    options?: CrmIntegrationsTypes,
  ) => {
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
      kind,
      options,
    });
  };

  const onInviteTeammateClick = () => {
    if (company) {
      openInviteUserModal(company, pagesWithAvailableSeats);
    }
  };
  const retriveInvites = async () => {
    if (company?.id) {
      await retrieveAll(company.id);
    }
  };

  const fetchGoogleCredentials = async () => {
    if (facebookPage?.id) {
      retrieveGoogleClientCredentials(facebookPage.id);
    }
  };

  const actions: ActionProps[] = [
    {
      keyName: 'xray',
      title: `Install ${pixelName}`,
      icon: <XRayIcon className={css.channelIcon} />,
      onClick: () => history.push(BotBuilderRoutes.xRay.getLink({ chatbotId })),
      completed: facebookPage?.getemailsActivated,
      buttonName: !facebookPage?.getemailsActivated ? 'Install' : undefined,
      tooltip: !facebookPage?.getemailsActivated
        ? 'Install Pixel'
        : 'View Pixels',
      buttonClassName: !facebookPage?.getemailsActivated
        ? css.xrayActionButton
        : '',
      component: (
        <span className={css.contentSubtitle}>
          Gather leads from your website traffic
        </span>
      ),
    },
    {
      keyName: 'integrations',
      title: 'Connect CRM',
      icon: <ApiOutlined className={css.channelIcon} />,
      onClick: () => navigateTo('native-integrations'),
      buttonName: 'Connect',
      component: crms && (
        <div className={css.crmsBlock}>
          {crms.map(crm => (
            <Tooltip
              key={uniqueId()}
              title={
                <div className={css.tooltipText}>
                  {humanizedIntegrationNames[crm.integrationType]}
                  {' is '}
                  {crm.active ? 'connected' : 'not connected'}
                </div>
              }
            >
              <div
                onClick={() =>
                  navigateTo(
                    'native-integrations',
                    !crm.active && crm.integrationType,
                  )
                }
              >
                <IntegrationIcon
                  integrationType={crm.integrationType}
                  isActive={crm.active}
                />
              </div>
            </Tooltip>
          ))}
        </div>
      ),
      disabled: !facebookPage?.getemailsActivated,
      completed: !!crms.filter(i => i?.active).length,
      hide: isShopify,
    },
    {
      keyName: 'add_domain',
      title: 'Add Sending Email',
      icon: <MailOutlined className={css.channelIcon} />,
      onClick: () => {
        history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
          kind: 'email',
        });
      },
      component: (
        <span className={css.contentSubtitle}>
          Send emails through verified domains
        </span>
      ),
      disabled: false,
      completed: false,
      buttonName: 'Add',
      hide: isShopify,
    },
    {
      keyName: 'ads',
      title: hasAdAccounts ? 'Manage Ad Accounts' : 'Connect Ad Accounts',
      icon: <NotificationOutlined className={css.channelIcon} />,
      onClick: () => {
        history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
          kind: 'facebook',
        });
      },
      component: !hasAdAccounts ? (
        <span className={css.contentSubtitle}>
          Send high intent data to ad platforms: Facebook and Google Ads
        </span>
      ) : (
        <div className={css.crmsBlock}>
          <FacebookIcon
            className={classNames({
              [css.facebookIconInactive]: !hasFacebookAdAccounts,
            })}
          />
          <AdWordIcon
            className={classNames(css.adWordIcon, {
              [css.adWordIconInactive]: !hasGoogleAdsAccount,
            })}
          />
        </div>
      ),
      disabled: !facebookPage?.getemailsActivated,
      completed: hasAdAccounts,
      tooltip: 'Connect Ad Accounts',
      hide: isShopify,
    },

    {
      keyName: 'users',
      title: 'Add Teammates',
      icon: <TeamOutlined className={css.channelIcon} />,
      onClick: () => onInviteTeammateClick(),
      tooltip: 'Add Teammates',
      component: !activeAndPendingUsers.length ? (
        <span className={css.contentSubtitle}>Delegate your tasks</span>
      ) : (
        <div className={css.crmsBlock}>
          {activeAndPendingUsers.map(
            (user, index) =>
              index < 5 && (
                <Tooltip
                  key={uniqueId()}
                  title={
                    <div className={css.tooltipText}>
                      {user.pending
                        ? `${user.email} has not accepted your invite`
                        : user.email}
                    </div>
                  }
                >
                  <UserFilled
                    className={classNames({
                      [css.activeUserIcon]: !user.pending,
                    })}
                  />
                </Tooltip>
              ),
          )}
          {activeAndPendingUsers.length > 5 && (
            <span style={{ marginLeft: '-12px' }}>...</span>
          )}
        </div>
      ),
      completed: activeAndPendingUsers?.length > 1,
      disabled: false,
      hide: isShopify,
    },
    {
      keyName: 'subscription',
      title: facebookPage?.subscription ? 'Upgrade Plan' : 'Choose Plan',
      icon: <CrownOutlined className={css.channelIcon} />,
      onClick: () => choosePlan(),
      hide: isShopify,
      component: facebookPage?.subscription ? (
        <div>
          Your Plan: <span style={{ fontWeight: 500 }}>{currentPlanName}</span>
        </div>
      ) : (
        <span className={css.contentSubtitle}>
          Maximize conversions from ads, emails, and customer journey insights
        </span>
      ),
      disabled: !facebookPage?.getemailsActivated,
      completed: !!facebookPage?.subscription,
      buttonName: 'View Pricing',
    },
  ];

  const getUsedCRMS = async () => {
    if (facebookPage) {
      const res = await retriveAddedCrms(facebookPage.id);

      const CRMList = (
        crmsList ||
        Object.keys(humanizedIntegrationNames).filter(
          crm => crm !== 'json_webhook',
        )
      ).map(integration => ({
        integrationType: integration,
        active: res?.crmConnections.includes(integration),
      }));
      setCrms(CRMList);
    }
  };

  useEffect(() => {
    getUsedCRMS();
    retriveInvites();
  }, [facebookPage?.id]);

  useEffect(() => {
    if (facebookPage?.id) {
      getAccounts(facebookPage.id);
    }
  }, [facebookPage?.id, getAccounts]);

  useEffect(() => {
    adAccount.fetchFacebookPageAdAccounts();
    fetchGoogleCredentials();
  }, []);

  return { actions };
};
